/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'
import { injectIntl } from 'react-intl'

import { EuiComboBox, EuiFormRow } from '@elastic/eui'

import { isCCMFeatureActivated } from '@modules/ccm-user-lib/feature'
import Feature from '@modules/utils/feature'

import { isFeatureActivated } from '@/store'

import { ProductType } from '../../../types'

import type { FunctionComponent } from 'react'
import type { Props } from './types'
import type { WrappedComponentProps } from 'react-intl'

const ProductTypeSelect: FunctionComponent<Props & WrappedComponentProps> = ({
  onChange,
  isLoading,
  intl: { formatMessage },
  values,
}) => {
  const options = [
    {
      label: formatMessage({
        id: 'billing-usage.filters.type.deployments',
        defaultMessage: 'Hosted deployments',
      }),
      value: ProductType.Deployments,
    },
  ]

  if (isFeatureActivated(Feature.serverless)) {
    options.push({
      label: formatMessage({
        id: 'billing-usage.filters.type.projects',
        defaultMessage: 'Serverless projects',
      }),
      value: ProductType.Projects,
    })
  }

  if (isCCMFeatureActivated()) {
    options.push({
      label: formatMessage({
        id: 'billing-usage.filters.type.ccm',
        defaultMessage: 'Connected clusters',
      }),
      value: ProductType.CCM,
    })
  }

  const isDisabled = isLoading || options.length === 1

  return (
    <EuiFormRow
      fullWidth={true}
      label={formatMessage({
        id: 'billing-usage.filters.type',
        defaultMessage: 'Cloud resources',
      })}
    >
      <EuiComboBox
        aria-label={formatMessage({
          id: 'billing-usage.filters.type',
          defaultMessage: 'Cloud resources',
        })}
        options={options}
        selectedOptions={options.filter(({ value }) => values.includes(value))}
        onChange={(productTypes) =>
          onChange({ productTypes: productTypes.map(({ value }) => value) as ProductType[] })
        }
        isClearable={false}
        isDisabled={isDisabled}
        fullWidth={true}
        placeholder={formatMessage({
          id: 'billing-usage.filters.type.placeholder',
          defaultMessage: 'Select cloud resource type',
        })}
      />
    </EuiFormRow>
  )
}

export default injectIntl(ProductTypeSelect)
